<template>
  <div class="config-container">
    <h2>Create New Agent</h2>
    <form @submit.prevent="submitConfig">
      <fieldset class="form-group">
        <label for="agent_name">Agent Name</label>
        <input id="agent_name" v-model="name" type="text" required @input="onNameInput" autocomplete="off" />
        <label for="agent_url">Agent URL</label>
        <div class="url-input-wrapper">
          <span class="url-prefix">https://</span>
          <input id="agent_url" v-model="subdomain" type="text" required pattern="^[a-z0-9\-]+$" autocomplete="off" />
          <span class="url-suffix">.chatnav.net</span>
        </div>
        <small>Only the subdomain is editable. The full URL will be: https://{subdomain}.chatnav.net</small>

        <div class="server-type-group">
          <label>Server Type</label>
          <div class="server-type-cards">
            <label
              v-for="opt in serverTypeOptions"
              :key="opt.value"
              class="server-type-card"
              :class="{ selected: serverType === opt.value }"
            >
              <input type="radio" :value="opt.value" v-model="serverType" />
              <span class="server-type-name">{{ opt.label }}</span>
              <span class="server-type-specs">
                <span>{{ opt.vcpu }} vCPU</span>
                <span>{{ opt.ram }} RAM</span>
                <span>{{ opt.ssd }} SSD</span>
              </span>
              <span class="server-type-price">{{ opt.price }}/mo</span>
            </label>
          </div>
        </div>

        <div v-if="effectiveHubAdmin" class="status-select">
          <label for="status">Status</label>
          <select id="status" v-model="status" class="styled-select">
            <option value="creating">Creating</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="trial">Trial</option>
            <option value="error">Error</option>
            <option value="health_check_failed">Health Check Failed</option>
          </select>
        </div>
      </fieldset>
      <div class="form-actions">
        <button type="submit">Create Agent</button>
        <button type="button" @click="goHome">Cancel</button>
      </div>
    </form>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { apiFetch } from '@/services/api';
import { getAccessToken, decodeToken } from '@/services/auth';
const toast = useToast();

export default {
  name: 'NewAgentConfig',
  props: {
    isHubAdmin: {
      type: Boolean,
      default: undefined
    }
  },
  data() {
    return {
      name: '',
      subdomain: '',
      status: 'creating',
      serverType: 'cx23',
      effectiveHubAdmin: false,
      serverTypeOptions: [
        { value: 'cx23', label: 'CX23', vcpu: '2', ram: '4 GB', ssd: '40 GB', price: '€4.49' },
        { value: 'cx33', label: 'CX33', vcpu: '4', ram: '8 GB', ssd: '80 GB', price: '€6.99' },
        { value: 'cx43', label: 'CX43', vcpu: '8', ram: '16 GB', ssd: '160 GB', price: '€12.49' },
        { value: 'cx53', label: 'CX53', vcpu: '16', ram: '32 GB', ssd: '320 GB', price: '€22.99' },
      ]
    }
  },
  created() {
    // Determine effective admin status: prop or JWT
    if (typeof this.isHubAdmin === 'boolean') {
      this.effectiveHubAdmin = this.isHubAdmin;
    } else {
      const payload = decodeToken(getAccessToken());
      this.effectiveHubAdmin = !!(payload && payload.hub_admin);
    }
  },
  methods: {
    onNameInput() {
      // Auto-fill subdomain from name (slugify: lowercase, alphanum and dashes only)
      this.subdomain = this.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },
    async submitConfig() {
      toast.info('Creating agent...');
      const statusToSend = this.effectiveHubAdmin ? this.status : 'creating';
      const payload = {
        name: this.name,
        dashboard_url: `https://${this.subdomain}.chatnav.net/analytics`,
        subdomain: this.subdomain,
        domain: 'chatnav.net',
        status: statusToSend,
        server_type: this.serverType
      };
      let toastMsg = null;
      try {
        const response = await apiFetch('/api/create-server', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.status === 409) {
          toastMsg = 'Subdomain already exists. Please choose another.';
          return;
        }
        if (!response.ok) {
          toastMsg = 'Failed to create server.';
          return;
        }
        // Optionally show a success toast here
      } catch (e) {
        toastMsg = 'Failed to create server: ' + e.message;
      } finally {
        if (toastMsg) toast.error(toastMsg);
        this.$router.push('/home');
      }
    },
    goHome() {
      this.$router.push('/home');
    }
  }
}
</script>


<style lang="scss" scoped>
@import "@/assets/css/new-agent.scss";
</style>